/**
 * Human-readable exchange-rate label for a PO line.
 *
 * The stored convention on a PO line is "1 reporting-currency unit = rate
 * units of the PO currency" (e.g. on an INR tenant a USD line carries
 * 0.010604, i.e. 1 INR = 0.010604 USD). Users read rates the other way round
 * — how much of their reporting currency one unit of the PO currency costs —
 * so invert for display: "1 USD = ₹94.30". The stored value is untouched.
 *
 * @param rate - Stored rate (string or number), 1 reporting = rate × PO-ccy
 * @param poCurrency - The line's transaction currency code (e.g. USD)
 * @param reportingCurrency - The tenant's reporting currency code (e.g. INR)
 * @returns e.g. "1 USD = ₹94.30"; "-" when the rate is missing/invalid; the raw
 *   rate when either currency code is unknown; "1 USD = 1 USD" style is
 *   collapsed to "1.00" when both codes match.
 */
export function formatExchangeRateDisplay(
  rate: string | number | null | undefined,
  poCurrency?: string | null,
  reportingCurrency?: string | null,
): string {
  if (rate === null || rate === undefined || rate === '') return '-';
  const numeric = typeof rate === 'number' ? rate : parseFloat(rate);
  if (!Number.isFinite(numeric) || numeric <= 0) return '-';
  if (!poCurrency || !reportingCurrency) return String(rate);
  if (poCurrency === reportingCurrency) return '1.00';

  const inverted = 1 / numeric;
  // Two decimals reads naturally for ordinary pairs; very small values (a
  // strong reporting currency) keep enough precision to be meaningful.
  const decimals = inverted >= 1 ? 2 : 4;
  let amount: string;
  try {
    amount = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: reportingCurrency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(inverted).replace('US$', '$');
  } catch {
    amount = `${inverted.toFixed(decimals)} ${reportingCurrency}`;
  }
  return `1 ${poCurrency} = ${amount}`;
}
